import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, EnvelopeSimple, MapPin, Phone } from "@phosphor-icons/react";
import { Reveal } from "@/components/site/Reveal";
import { VERTICAL_OPTIONS } from "@/lib/content";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = '/api';

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    vertical_interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Salaam. We’ve received your note and will write back soon.");
      setForm({ name: "", email: "", phone: "", vertical_interest: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-ocean/20 focus:border-ocean focus:outline-none py-3 text-lg placeholder:text-ocean/40 transition-colors";

  return (
    <main data-testid="contact-page" className="bg-parchment text-ocean">
      {/* HERO */}
      <section className="pt-40 pb-12 md:pt-48 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <div className="overline text-spice">Contact</div>
            <h1 className="font-serif-display text-6xl md:text-8xl mt-6 leading-[0.85]">
              Say <em className="text-spice">hello.</em>
              <br />
              We answer everyone.
            </h1>
          </div>
          <div className="md:col-span-5 self-end">
            <p className="text-ocean/75 text-lg leading-relaxed">
              Whether you’re a college student looking to start a Yuva chapter, a founder who wants to
              speak at YiBE, or just curious about Yi — drop us a note.
            </p>
          </div>
        </div>
      </section>

      {/* FORM + DETAILS */}
      <section className="pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-16">
          {/* FORM */}
          <motion.form
            data-testid="contact-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-7 space-y-10"
          >
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
              <label className="block">
                <div className="overline text-ocean/50 mb-2">Your name *</div>
                <input
                  data-testid="contact-input-name"
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Aiswarya Menon"
                  className={inputCls}
                />
              </label>
              <label className="block">
                <div className="overline text-ocean/50 mb-2">Email *</div>
                <input
                  data-testid="contact-input-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@calicut.in"
                  className={inputCls}
                />
              </label>
              <label className="block">
                <div className="overline text-ocean/50 mb-2">Phone</div>
                <input
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+91 ..."
                  className={inputCls}
                />
              </label>
              <label className="block">
                <div className="overline text-ocean/50 mb-2">I’m interested in</div>
                <Select
                  value={form.vertical_interest}
                  onValueChange={(v) => setForm((f) => ({ ...f, vertical_interest: v }))}
                >
                  <SelectTrigger
                    data-testid="contact-select-vertical"
                    className="w-full bg-transparent border-0 border-b border-ocean/20 rounded-none h-auto py-3 px-0 text-lg shadow-none focus:ring-0 focus:border-ocean text-ocean"
                  >
                    <SelectValue placeholder="Pick a vertical" />
                  </SelectTrigger>
                  <SelectContent className="bg-parchment border border-ocean/15">
                    {VERTICAL_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt} data-testid={`vertical-option-${opt.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
            </div>

            <label className="block">
              <div className="overline text-ocean/50 mb-2">Your message *</div>
              <textarea
                data-testid="contact-input-message"
                required
                rows={5}
                value={form.message}
                onChange={update("message")}
                placeholder="Tell us what you’d like to do, or what you’d like to know."
                className={`${inputCls} resize-none`}
              />
            </label>

            <div className="pt-4">
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={submitting}
                className="overline px-8 py-5 bg-ocean text-parchment hover:bg-spice disabled:opacity-60 transition-colors inline-flex items-center gap-2"
              >
                {submitting ? "Sending..." : "Send message"} <ArrowUpRight size={16} weight="bold" />
              </button>
            </div>
          </motion.form>

          {/* DETAILS */}
          <Reveal className="md:col-span-4 md:col-start-9 space-y-12">
            <div>
              <div className="overline text-spice mb-6">Find us</div>
              <ul className="space-y-6">
                <li className="flex gap-4 items-start" data-testid="contact-info-address">
                  <MapPin size={20} weight="light" className="text-spice mt-1 shrink-0" />
                  <div>
                    <div className="font-serif text-lg">Yi Kozhikode Chapter</div>
                    <div className="text-ocean/70 text-sm leading-relaxed">
                      c/o CII Office<br />
                      Kozhikode, Kerala<br />
                      India
                    </div>
                  </div>
                </li>
                <li className="flex gap-4 items-start" data-testid="contact-info-email">
                  <EnvelopeSimple size={20} weight="light" className="text-spice mt-1 shrink-0" />
                  <div>
                    <div className="font-serif text-lg">Write to us</div>
                    <a href="mailto:kozhikode@youngindians.net" className="text-ocean/70 text-sm hover:text-spice">
                      kozhikode@youngindians.net
                    </a>
                  </div>
                </li>
                <li className="flex gap-4 items-start" data-testid="contact-info-phone">
                  <Phone size={20} weight="light" className="text-spice mt-1 shrink-0" />
                  <div>
                    <div className="font-serif text-lg">Chapter desk</div>
                    <div className="text-ocean/70 text-sm">+91 · coming soon</div>
                  </div>
                </li>
              </ul>
            </div>

            <div className="border-t border-ocean/10 pt-8">
              <div className="overline text-ocean/50 mb-3">Coordinates</div>
              <div className="font-serif text-2xl">11°15′N · 75°46′E</div>
              <p className="text-ocean/60 text-sm mt-2 leading-relaxed">
                Same coast as the Zamorin’s court. Same horizon Ibn Battuta walked into.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
